import { ErrorMessage, Form, Formik, FormikProps } from 'formik'
import moment from 'moment'
import { FC, useEffect, useState } from 'react'
import styled from 'styled-components'
import * as Yup from 'yup'

import { Field } from 'mobileApp/common/component/formik'
import { InsuranceClause } from 'mobileApp/common/component/InsuranceClause'
import { PAYMENT_CLAUSE_MAP, PaymentType } from 'mobileApp/constants/constants'
import { LtcLayout } from 'mobileApp/ltc/layout'
import { authStore, ltcProductCategory, ltcStore, premiumStore } from 'mobileApp/store'

enum Code {
  Loading,
  Ready,
  Failed,
}

export const S04Coverage = () => {
  const [status, setStatus] = useState({ code: Code.Loading, message: '' })

  useEffect(() => {
    premiumStore
      .getPremiums(ltcProductCategory)
      .then(() => ltcStore.quote())
      .then(() => setStatus({ code: Code.Ready, message: '' }))
      .catch(({ message }) => setStatus({ code: Code.Failed, message }))
  }, [])

  const renderBody = () => {
    if (status.code === Code.Loading) return <Loading />
    if (status.code !== Code.Ready) return <FailedMsg msg={status.message} />

    return <CoverageFormik />
  }

  return (
    <LtcLayout title="承保範圍" step={3}>
      {renderBody()}
    </LtcLayout>
  )
}

const Loading = () => (
  <div className="row">
    <div className="col-12 text-primary">
      載入中...
      <div className="spinner-border spinner-border-sm ml-2" />
    </div>
  </div>
)

const FailedMsg: FC<{ msg: String }> = ({ msg }) => (
  <div className="row">
    <div className="col-12 text-danger">{msg}</div>
  </div>
)

const CoverageFormik = () => {
  return (
    <Formik
      initialValues={{ paymentType: ltcStore.paymentType }}
      onSubmit={(values, { setStatus, setSubmitting }) => {
        setStatus()
        ltcStore.paymentType = values.paymentType
        setSubmitting(false)
        ltcStore.navi.goNext()
      }}
      validationSchema={Yup.object().shape({ paymentType: Yup.string().trim().required('必填欄位！') })}
      component={formikProps => CoverageForm(formikProps)}
    />
  )
}

const CoverageForm = ({ values: { paymentType }, isSubmitting }: FormikProps<{ paymentType: string }>) => {
  const { maintainCenter } = authStore.currUser
  const { paymentOptions } = ltcStore
  const curPremium = paymentOptions.filter(premium => premium.paymentType === paymentType)[0]
  const { premium, fixDeductible, chgDeductible } = curPremium || {}

  const yearAmount = ((paymentType === '3Y' || paymentType === 'M') && 3) || (paymentType === '2Y' && 2) || 1

  const paymentDependentClause = PAYMENT_CLAUSE_MAP[paymentType as PaymentType]

  return (
    <Form>
      <section className="mb-3">
        <div>承保行動裝置因不可預料之事故所致毀損</div>
        <div className="text-primary">（賠償方式僅限原機維修或置換）</div>
      </section>

      <section className="row align-items-center mb-3">
        <div className="col-12 col-sm-4 text-primary">保險期間</div>
        <div className="col-12 col-sm-8">{`${moment().format('YYYY／M／D')} ～ ${moment()
          .add(yearAmount, 'year')
          .subtract(1, 'day')
          .format('YYYY／M／D')}`}</div>
      </section>

      <section className="row align-items-center mb-3">
        <div className="col-12 col-sm-4 text-primary">保險期間累積最高賠償次數</div>
        <div className="col-5 col-sm-2 fs_caption text-muted">
          第一年<span className="h5 text-dark px-2 m-0">3</span>次
        </div>
        {(paymentType === 'M' || paymentType === '2Y' || paymentType === '3Y') && (
          <div className="col-5 col-sm-2 fs_caption text-muted">
            第二年<span className="h5 text-dark px-2 m-0">3</span>次
          </div>
        )}
        {(paymentType === 'M' || paymentType === '3Y') && (
          <div className="col-5 col-sm-2 fs_caption text-muted">
            第三年<span className="h5 text-dark px-2 m-0">3</span>次
          </div>
        )}
      </section>

      <section className="row align-items-center mb-3">
        <div className="col-12 col-sm-4 text-primary">自負額</div>
        <div className="col-5 col-sm-4 fs_caption text-muted">
          原機維修<span className="h5 text-dark px-2 m-0">{fixDeductible}</span>元
        </div>
        <div className="col-5 col-sm-4 fs_caption text-muted">
          置換<span className="h5 text-dark px-2 m-0">{chgDeductible}</span>元
        </div>
      </section>

      <section className="row align-items-center mb-3">
        <div className="col-12 col-sm-4 text-primary">指定維修中心</div>
        <div className="col-12 fs_body1 col-sm-8">{maintainCenter}</div>
      </section>

      <section className="row align-items-center mb-3">
        <div className="col-12 col-sm-4 text-primary">保險費</div>
        <div className="col-12 col-sm-8 fs_caption text-muted">
          新台幣<span className="h5 text-dark px-2 m-0">{premium}</span>元
        </div>
      </section>

      <section className="row align-items-center mb-3">
        <div className="col-sm-4 text-primary">繳款方式</div>
        <div className="col-sm-8">
          <RadioBox>
            <Field type="radio" name="paymentType" id="M" value="M" checked={paymentType === 'M'} className="d-none" />
            <label htmlFor="M">月繳（36M）</label>
            <ErrorMessage name="paymentType" component="div" className="invalid-feedback" />
          </RadioBox>

          <RadioBox>
            <Field
              type="radio"
              name="paymentType"
              id="1Y"
              value="1Y"
              checked={paymentType === '1Y'}
              className="d-none"
            />
            <label htmlFor="1Y">年繳（1Y）</label>
          </RadioBox>

          <RadioBox>
            <Field
              type="radio"
              name="paymentType"
              id="2Y"
              value="2Y"
              checked={paymentType === '2Y'}
              className="d-none"
            />
            <label htmlFor="2Y">年繳（2Y）</label>
          </RadioBox>

          <RadioBox>
            <Field
              type="radio"
              name="paymentType"
              id="3Y"
              value="3Y"
              checked={paymentType === '3Y'}
              className="d-none"
            />
            <label htmlFor="3Y">年繳（3Y）</label>
          </RadioBox>
        </div>
      </section>

      {paymentDependentClause && (
        <InsuranceClause
          label="本保險契約適用附加條款"
          btnText={paymentDependentClause.title}
          modalId={paymentDependentClause.id}
          contentFn={paymentDependentClause.content}
        />
      )}

      <section className="row mb-3">
        <div className="col-sm-4 text-primary"></div>
        <div className="col-sm-8 text-danger mt-sm-0">請詳閱以上保單條款內容</div>
      </section>

      <section className="my-3 d-flex justify-content-between">
        <button type="button" className="btn btn-light" onClick={ltcStore.navi.goBack}>
          上一步
        </button>
        <button type="submit" disabled={isSubmitting} className="btn btn-secondary">
          下一步
        </button>
      </section>
    </Form>
  )
}

const RadioBox = styled.div`
  display: inline-block;
  margin-right: 1rem;

  label {
    display: inline-block;
    padding: 4px 16px;
    margin-top: 0.5rem;
    font-size: 18px;
    color: #008fcb;
    background-color: #ffffff;
    border: solid 1px #008fcb;
    border-radius: 2px;
    cursor: pointer;
  }

  input:checked + label {
    font-weight: 500;
    color: #ffffff;
    background-color: #008fcb;
  }
`
